import React, { useState, useEffect } from 'react';
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar } from '@mui/material';
import axios from 'axios';
import { Constants } from '../../endPoints';
import { jwtDecode } from 'jwt-decode';
import Breadcumb from '../../Layouts/Breadcumb';
import DashboardNav from './DashboardNav';
import Swal from 'sweetalert2';
import { FaRegComments } from 'react-icons/fa';

const ChatInbox = () => {
    const [chatList, setChatList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showChatBox, setShowChatBox] = useState(false);
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [chatMessage, setChatMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    useEffect(() => {
        fetchChatList(page);
    }, [page]);

    const fetchChatList = async (currentPage) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            const decoded = jwtDecode(token);

            const response = await axios.post(`${Constants.getChatListForProductOwner}`, {
                user_id: decoded.id,
                page: currentPage,
                limit,
            });

            if (response.data.status === 1) {
                setChatList(response.data.list);
            }
        } catch (error) {
            console.error('Error fetching chat list:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchChatHistory = async (product_id, buyer_id) => {
        try {
            const token = localStorage.getItem('authToken');
            const decoded = jwtDecode(token);
            const receiver_id = decoded.id;

            await axios.post(`${Constants.markMessagesSeen}`, { product_id, sender_id: buyer_id, receiver_id });

            const response = await axios.post(`${Constants.getMyChatHistory}`, { product_id, sender_id: buyer_id, receiver_id });

            if (response.data.status === 1) {
                const formatted = response.data.data.map(msg => ({
                    text: msg.chat_msg,
                    time: new Date(msg.created_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ", " +
                        new Date(msg.created_time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                    sender: msg.sender_id === buyer_id ? 'other' : 'me',
                    image_url: msg.image_url || null
                }));
                setChatHistory(formatted);

                setChatList(prev => prev.map(chat =>
                    chat.product_id === product_id && chat.buyer_id === buyer_id
                        ? { ...chat, unread_count: 0 }
                        : chat
                ));
            }
        } catch (error) {
            console.error('Error fetching chat history:', error);
        }
    };

    const handleSelectImage = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage({
                file,
                preview: URL.createObjectURL(file)
            });
        }
    };

    const handleSendMessage = async () => {
        if (!chatMessage.trim() && !selectedImage) return;
        try {
            const token = localStorage.getItem('authToken');
            const decoded = jwtDecode(token);
            const formData = new FormData();
            formData.append('sender_id', decoded.id);
            formData.append('receiver_id', selectedChat.buyer_id);
            formData.append('product_id', selectedChat.product_id);
            formData.append('msg', chatMessage);
            if (selectedImage?.file) formData.append('image', selectedImage.file);

            await axios.post(`${Constants.createChatMessage}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setChatMessage('');
            setSelectedImage(null);
            fetchChatHistory(selectedChat.product_id, selectedChat.buyer_id);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleDeleteChat = async (product_id, buyer_id) => {
        const token = localStorage.getItem('authToken');
        const decoded = jwtDecode(token);
        const owner_id = decoded.id;

        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#B3902D",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.post(`${Constants.deleteChat}`, {
                        product_id,
                        buyer_id,
                        owner_id
                    });

                    if (response.data.status === 1) {
                        Swal.fire("Deleted!", "Chat has been deleted.", "success");
                        fetchChatList(page);
                    } else {
                        Swal.fire("Failed!", "Chat could not be deleted.", "error");
                    }
                } catch (error) {
                    console.error('Error deleting chat:', error);
                    Swal.fire("Error!", "Something went wrong.", "error");
                }
            }
        });
    };

    useEffect(() => {
        const chatBox = document.querySelector(".p-3.h-60.overflow-y-auto");
        if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
    }, [chatHistory]);

    return (
        <>
            <Breadcumb pagetitle="Chat Inbox" />
            <section className="py-12">
                <div className="container mx-auto px-4">
                    <Grid container spacing={5}>
                        <Grid item lg={3}><DashboardNav /></Grid>
                        <Grid item lg={9}>
                            {isLoading ? <p>Loading chats...</p> : (
                                <TableContainer className="shadow-lg rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#fbf8f1' }}>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#232323' }}>Product</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#232323' }}>Buyer</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#232323' }}>Last Message</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#232323' }}>Unread</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: '#232323' }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {chatList.map((chat, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            {chat.product_imgUrl && <Avatar src={chat.product_imgUrl} variant="square" />}
                                                            <span>{chat.product_name || 'N/A'}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            {chat.buyer_image && <Avatar src={chat.buyer_image} />}
                                                            <span>{chat.buyer_name}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{chat.last_msg || 'No message'}</TableCell>
                                                    <TableCell>
                                                        {chat.unread_count > 0 ? (
                                                            <span className="inline-block px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded-full">
                                                                {chat.unread_count} Unread
                                                            </span>
                                                        ) : (
                                                            <span className="inline-block px-2 py-1 text-xs font-medium text-gray-400 bg-gray-100 rounded-full">
                                                                No Unread
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-4">
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedChat(chat);
                                                                    setShowChatBox(true);
                                                                    fetchChatHistory(chat.product_id, chat.buyer_id);
                                                                }}
                                                                className="text-[#B3902D]"
                                                            >
                                                                <FaRegComments size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteChat(chat.product_id, chat.buyer_id)}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {/* Pagination */}
                                    <div className="flex justify-center items-center gap-6 py-4 bg-white border-t">
                                        <button
                                            onClick={() => {
                                                if (page > 1) setPage(prev => prev - 1);
                                            }}
                                            disabled={page === 1}
                                            className="px-4 py-2 bg-[#B3902D] text-white rounded-full hover:bg-[#a27800] disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-gray-700 font-semibold">Page {page}</span>
                                        <button
                                            onClick={() => setPage(prev => prev + 1)}
                                            className="px-4 py-2 bg-[#B3902D] text-white rounded-full hover:bg-[#a27800]"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </TableContainer>
                            )}
                        </Grid>
                    </Grid>

                    {/* Chat Box */}
                    {showChatBox && selectedChat && (
                        <div className="fixed bottom-5 right-10 w-72 shadow-lg rounded border bg-white z-50">
                            <div className="bg-[#B3902D] text-white px-4 py-2 rounded-t flex justify-between items-center">
                                <span className="font-semibold">Chat</span>
                                <button onClick={() => setShowChatBox(false)} className="text-white font-bold text-xl">&times;</button>
                            </div>

                            <div className="p-3 h-60 overflow-y-auto">
                                {chatHistory.map((msg, index) => (
                                    <div key={index} className={`mb-3 flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${msg.sender === 'me' ? 'bg-blue-100' : 'bg-orange-100'}`}>
                                            {msg.image_url && <img src={msg.image_url} alt="Chat Image" className="w-32 h-auto rounded mb-2" />}
                                            {msg.text && <div>{msg.text}</div>}
                                            <div className="text-[10px] text-gray-500 mt-1 text-right">{msg.time}</div>
                                        </div>
                                    </div>
                                ))}

                                {selectedImage && (
                                    <div className="mb-3 flex justify-end">
                                        <div className="max-w-[75%] px-3 py-2 rounded-lg text-sm bg-blue-100">
                                            <img src={selectedImage.preview} alt="Selected Preview" className="w-32 h-auto rounded mb-2" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex border-t">
                                <input
                                    type="file"
                                    onChange={handleSelectImage}
                                    className="hidden"
                                    id="imageUpload"
                                />
                                <label htmlFor="imageUpload" className="bg-[#B3902D] text-white px-3 flex items-center justify-center cursor-pointer">
                                    <i className="fa fa-paperclip text-sm"></i>
                                </label>
                                <textarea
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    rows="1"
                                    className="flex-grow p-2 border-0 focus:outline-none text-sm"
                                    placeholder="Type your message here"
                                    value={chatMessage}
                                    onChange={(e) => setChatMessage(e.target.value)}
                                />
                                <button onClick={handleSendMessage} className="bg-black text-white px-3 flex items-center justify-center">
                                    <i className="fa fa-paper-plane text-sm"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default ChatInbox;
